import { Box, Divider, Flex, Grid } from '@chakra-ui/react';
import { Button, ChevronRight } from '@mesh.ai/rabbit';
import { useInvalidateGetDisciplinaryActions } from 'Deer/pages/MyData/DisciplinaryActions/dataHooks';
import { useToast } from 'components/hooks/useToast';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { checkDisciplinaryActionGenAIEnabled } from 'utils/helpers/featureFlags';
import uuidv4 from 'utils/uuidv4';

import { TOAST_STATUSES } from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';

import { DrawerFooter, primaryDrawerWidth } from 'uiToolkitV1/BottomDrawer';
import MavenButton from 'uiToolkitV1/MavenButton/MavenButton';
import { RichTextEditor2 } from 'uiToolkitV1/RichTextEditor2';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { createDisciplinaryAction, getPastNotes } from '../hooks/addViolation';
import { useCreateViolationAnalysisMutation } from '../hooks/meshMavenDataHook';
import { updateAddViolationDrawer, useSelectAddViolationDrawerData } from '../slice/addViolation.slice';
import { DateAndTimeDropdown } from './DateAndTimeDropdown';
import { SearchEmployee } from './SearchEmployee';
import { SeverityDropdown } from './SeverityDropdown';
import { ViolationTimeline } from './ViolationTimeline';
import { ViolationTypeDropdown } from './ViolationTypeDropdown';

const CreateViolationPrimaryPanel = ({ onCloseDrawer }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { descriptionHtml, description, raisedFor, severity, type, reportedTime, revisions, pastNotes } =
    useSelectAddViolationDrawerData();

  const invalidateDisciplinaryActions = useInvalidateGetDisciplinaryActions();

  const { mutate: getViolationAnalysis } = useCreateViolationAnalysisMutation();

  const isMeshMavenDisabled = !description || description.trim().length < 15 || !raisedFor?.id;
  const isSaveDisabled = !description || description.trim().length === 0 || !raisedFor?.id;

  const onSaveHandler = async () => {
    try {
      const formattedDateTime = format(reportedTime, "yyyy-MM-dd'T'HH:mm:ss");
      await createDisciplinaryAction({
        description,
        descriptionHtml,
        raisedFor: raisedFor.id,
        reportedTime: formattedDateTime,
        severity,
        type,
      });
      toast({
        title: 'Disciplinary action created successfully',
        status: TOAST_STATUSES.SUCCESS,
      });
      onCloseDrawer();
      invalidateDisciplinaryActions();
    } catch (error) {
      console.error('Error creating disciplinary action:', error);
      toast({
        title: 'Failed to create disciplinary action',
        status: TOAST_STATUSES.ERROR,
      });
    }
  };

  const handleGetPastNotes = async (employeeId) => {
    try {
      const res = await getPastNotes(employeeId);
      dispatch(
        updateAddViolationDrawer({
          pastNotes: res.rows ?? [],
        })
      );
    } catch (error) {
      console.error('Error fetching past notes:', error);
      toast({
        title: 'Failed to fetch past notes',
        status: TOAST_STATUSES.ERROR,
      });
    }
  };

  return (
    <Flex w={primaryDrawerWidth} flexDir="column" border="4px solid #EBEFF3" borderRadius="8px" h="100%">
      <Box overflowY="auto" h="100%" p="12px">
        <TextBodyMedium bold>Add disciplinary action</TextBodyMedium>
        <Box p={{ base: '16px 0', desktop: '16px 16px 8px' }}>
          <ViolationTimeline status={null} />
        </Box>

        <Divider mt="12px" mb={{ base: '24px', desktop: '12px' }} />
        <Flex flexDir="column" alignItems="flex-end" gap="8px" mb="12px">
          <Flex flexDir="column" alignItems="flex-start" width="100%">
            <TextLabel color="text.g2">Employee being reported</TextLabel>
            <SearchEmployee
              onSelect={(emp) => {
                dispatch(
                  updateAddViolationDrawer({
                    raisedFor: {
                      ...emp,
                      id: Number(emp?.id),
                      displayName: emp?.displayName,
                    },
                  })
                );
                handleGetPastNotes(emp?.id);
              }}
              employeeMiniProfile={raisedFor}
              onClear={() => {
                dispatch(
                  updateAddViolationDrawer({
                    raisedFor: undefined,
                    pastNotes: [],
                  })
                );
              }}
              placeholder="Type employee name"
            />
          </Flex>
          {pastNotes.length > 0 && (
            <Button
              variant="text"
              iconRight={ChevronRight}
              onClick={() => {
                dispatch(
                  updateAddViolationDrawer({
                    isPastNotesPanelOpen: true,
                  })
                );
              }}
            >
              Past notes
            </Button>
          )}
        </Flex>
        <Grid templateColumns="repeat(2, 1fr)" gap={3} mb={4}>
          <ViolationTypeDropdown />
          <SeverityDropdown />
        </Grid>
        <DateAndTimeDropdown />
        <Box position="relative" mb={4}>
          <TextLabel color="text.g2">Description</TextLabel>
          <Flex
            sx={{
              '.character-count': {
                maxWidth: '100%',
              },
              '& .rte-editor-2.ProseMirror': {
                minHeight: '150px',
              },
              ...(isMobileView()
                ? {
                    '& .editor-toolbar': {
                      flexWrap: 'wrap',
                      width: '50%',
                    },
                  }
                : {}),
            }}
            flexDir="column"
          >
            <RichTextEditor2
              editorMaxHeight="180px"
              alwaysShowMenuBar
              placeholder="Description"
              onChange={(htmlText, plainText) => {
                dispatch(
                  updateAddViolationDrawer({
                    description: plainText,
                    descriptionHtml: htmlText,
                  })
                );
              }}
              content={descriptionHtml}
            />
          </Flex>
          {checkDisciplinaryActionGenAIEnabled() && (
            <Flex position="absolute" right="0" bottom="0" p="8px">
              <Tooltip
                label={
                  isMeshMavenDisabled
                    ? 'To improve the quality of analysis, please enter a minimum of 15 characters.'
                    : null
                }
                placement="bottom-end"
              >
                <MavenButton
                  // @ts-expect-error ignore
                  text="Help me write"
                  onClick={() => {
                    const newRevision = {
                      requestId: '',
                      originalState: description,
                      updatedState: description,
                      isAIFeedbackProvided: false,
                      isReanalyzeEnabled: false,
                      feedbackAnalysis: { context: 0, observations: 0, impact: 0, next_steps: 0 },
                    };

                    dispatch(
                      updateAddViolationDrawer({
                        revisionNumber: revisions.length,
                        revisions: [...revisions, newRevision],
                        isSecondaryPanelOpen: true,
                        rephrasedDescription: description,
                        rephrasedDescriptionHtml: descriptionHtml,
                        meshMavenGroupId: uuidv4(),
                      })
                    );

                    getViolationAnalysis({
                      revisionNumber: revisions.length,
                      description,
                      groupId: uuidv4(),
                      revisions: [...revisions, newRevision],
                    });
                  }}
                  isDisabled={isMeshMavenDisabled}
                />
              </Tooltip>
            </Flex>
          )}
        </Box>
      </Box>
      <DrawerFooter>
        <Flex justifyContent="space-between" w="100%">
          <Button variant="transparentOutline" onClick={onCloseDrawer}>
            Cancel
          </Button>
          <Button onClick={onSaveHandler} disabled={isSaveDisabled}>
            Save
          </Button>
        </Flex>
      </DrawerFooter>
    </Flex>
  );
};

export { CreateViolationPrimaryPanel };
